import type { Platform } from '@/shared/utils/platform';

/**
 * Base class for all notification-related errors.
 * Allows consumers to catch all notification errors with a single check.
 */
export class NotificationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'NotificationError';
  }
}

/**
 * User denied notification permission (clicked "Don't Allow" or similar).
 * On iOS this is often a soft denial that can be retried.
 * On Web/Android this may require manual settings change.
 */
export class NotificationPermissionDeniedError extends NotificationError {
  constructor(public readonly platform: Platform) {
    super(`Notification permission denied on ${platform}`);
    this.name = 'NotificationPermissionDeniedError';
  }
}

/**
 * Web notifications are permanently blocked in browser settings.
 * User must manually enable notifications in browser/site settings.
 */
export class NotificationBrowserBlockedError extends NotificationError {
  constructor() {
    super('Notifications are blocked in browser settings');
    this.name = 'NotificationBrowserBlockedError';
  }
}

/**
 * Timeout waiting for push token registration.
 * Common on iOS when APNs is slow or device has connectivity issues.
 */
export class NotificationTokenTimeoutError extends NotificationError {
  constructor(public readonly timeoutMs: number) {
    super(`Push token registration timed out after ${String(timeoutMs)}ms`);
    this.name = 'NotificationTokenTimeoutError';
  }
}

/**
 * Push notifications are not supported on this platform/browser.
 * Examples: older browsers, private browsing mode, unsupported devices.
 */
export class NotificationUnsupportedError extends NotificationError {
  constructor(public readonly reason?: string) {
    super(reason ?? 'Push notifications are not supported on this platform');
    this.name = 'NotificationUnsupportedError';
  }
}

/**
 * Native push registration failed with a platform-specific error.
 * Contains the original error message from the native layer.
 */
export class NotificationRegistrationError extends NotificationError {
  constructor(public readonly originalError: string) {
    super(`Push registration failed: ${originalError}`);
    this.name = 'NotificationRegistrationError';
  }
}

/**
 * Push service error from the browser's Push API.
 * Common with Opera browser and when push services are unavailable.
 * Contains the original error name and message from the Push API.
 */
export class NotificationPushServiceError extends NotificationError {
  constructor(
    public readonly originalName: string,
    public readonly originalMessage: string,
  ) {
    super(`Push service error: ${originalMessage}`);
    this.name = 'NotificationPushServiceError';
  }
}
